import type { IAssignmentEvent, IAssignmentLogger } from '@ecs-na/access-flag-service';
import { EventName, postUiLog } from '../uilog';
import { recordAssignment } from './eppo-assignment-store';

const isDevelopment = process.env.GATSBY_ENV === 'development';

/**
 * Builds the Eppo assignment logger.
 *
 * Eppo emits an assignment event each time a subject is bucketed into a
 * variation. We forward those into the site's standard analytics pipeline via
 * `postUiLog` (Ensighten `window.ECS.tms` + `POST /api/clickstream/uilog`) as an
 * `event_experimentationLog` event — the same pipeline every other tracked event
 * uses. This mirrors the Angular app, which calls its `EcsAnalyticsService` from
 * `logAssignment`; `postUiLog` is this repo's equivalent analytics entry point.
 *
 * `postUiLog` assigns the payload to `window.ECS.tms.data.response`, so the
 * object below is the `response` body directly (no extra wrapper). In
 * development we additionally log the raw event to the console for visibility;
 * the analytics call still runs and is a safe no-op when the `window.ECS.tms`
 * globals are not present.
 */
export function createAssignmentLogger(): IAssignmentLogger {
  return {
    logAssignment(event: IAssignmentEvent): void {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.debug('[feature-flags] eppo assignment', event);
      }

      // Record the raw assignment first: it is the strongest available proof
      // that Eppo bucketed this subject (rather than a boolean default
      // fallback), and pages gate their reveal/attribution on it.
      recordAssignment(event);

      try {
        const {
          experiment,
          subject,
          variation,
          timestamp,
          featureFlag,
          allocation,
          holdoutKey,
          holdoutVariation,
          subjectAttributes = {}
        } = event;

        // The service flattens FlagUser.secondaryIds and .subjectAttributes onto
        // the event's subjectAttributes before evaluation, so identity values
        // resolveFlagUser set (subjectType, and ecsStaticId for returning users)
        // and context (pageid/pc/cc/ref/sessionid) all arrive here. This is the
        // only channel to read them back. We lift identity to dedicated top-level
        // fields and re-emit ONLY context in subjectAttributes (see below).
        const { subjectType, ecsStaticId, sessionid, pageid, pc, cc, ref } =
          subjectAttributes as Record<string, unknown>;

        // secondaryIds carries only correlating identifiers — never the subject
        // or subjectType (those are dedicated fields; duplicating them here is
        // what the requirement forbids). ecsStaticId is present only for
        // returning users (subject = marketingid); for new/anonymous users it IS
        // the subject, so it is absent and secondaryIds is left empty.
        const secondaryIds: Record<string, unknown> = {};
        if (typeof ecsStaticId === 'string' && ecsStaticId) {
          secondaryIds.ecsStaticId = ecsStaticId;
        }

        // Context only. Explicitly picked (not spread) so flattened identity
        // fields never leak back onto the emitted subjectAttributes.
        const contextAttributes: Record<string, unknown> = { platform: 'public' };
        if (pageid) {
          contextAttributes.pageid = pageid;
        }
        if (pc) {
          contextAttributes.pc = pc;
        }
        if (cc) {
          contextAttributes.cc = cc;
        }
        if (ref) {
          contextAttributes.ref = ref;
        }
        if (sessionid) {
          contextAttributes.sessionid = sessionid;
        }

        const postData = {
          experiment: experiment ?? undefined,
          subject,
          subjectType,
          // sessionid is context, reported here as the sub-entity, not an
          // identity value. Omitted entirely when absent, matching how
          // contextAttributes conditionally includes its fields.
          ...(sessionid ? { subEntity: sessionid } : {}),
          secondaryIds,
          variation: variation ?? '',
          timestamp,
          subjectAttributes: contextAttributes,
          featureFlag,
          allocation: allocation ?? undefined,
          holdoutKey,
          holdoutVariation
        };

        postUiLog(EventName.experimentationLog, postData);

        // postUiLog mutates window.ECS.tms.data.response; clear it so the
        // experimentation payload does not leak into later events.
        if (typeof window !== 'undefined' && window.ECS?.tms?.data) {
          delete window.ECS.tms.data.response;
        }
      } catch {
        // Assignment logging must never break flag evaluation.
      }
    }
  };
}
